import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LoadingScreen from "../components/Loading";
import { usePermission } from "@src/presentation/hooks/usePermission";
import type { Permission } from "@src/shared/constants/permissions";
import { getOnboardingRoute } from "@src/application/users/dto/auth-response.dto";

interface ProtectedRouteProps {
  /** Single permission required to access route */
  permission?: Permission | string;
  /** Array of permissions - user must have ALL to access route */
  requiredPermissions?: (Permission | string)[];
  /** Array of permissions - user must have ANY ONE to access route */
  anyPermissions?: (Permission | string)[];
}

/**
 * ProtectedRoute - Guards routes that require authentication AND complete profile
 * - Redirects to login if not authenticated
 * - Redirects to appropriate onboarding page if profile incomplete
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  permission,
  requiredPermissions,
  anyPermissions,
}) => {
  const { isAuthenticated, loading, initializing, isProfileComplete, profileState } = useAuth();
  const { hasPermission, hasAllPermissions, hasAnyPermission, isSuperAdmin } = usePermission();
  const location = useLocation();

  // Show loading during initial auth check
  if (initializing || loading) {
    return <LoadingScreen />;
  }

  // Not authenticated - redirect to auth
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // Authenticated but profile incomplete - redirect to onboarding
  if (!isProfileComplete && profileState) {
    const onboardingRoute = getOnboardingRoute(profileState);
    return <Navigate to={onboardingRoute} replace />;
  }

  // If no permissions are specified, just check authentication
  if (!permission && !requiredPermissions && !anyPermissions) {
    return <Outlet />;
  }

  // Super admins always have access
  if (isSuperAdmin()) {
    return <Outlet />;
  }

  // Check permissions
  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (requiredPermissions && requiredPermissions.length > 0) {
    hasAccess = hasAllPermissions(requiredPermissions);
  } else if (anyPermissions && anyPermissions.length > 0) {
    hasAccess = hasAnyPermission(anyPermissions);
  }

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

/**
 * OnboardingRoute - Guards onboarding routes
 * - Requires authentication (OTP verified)
 * - Requires profile to be INCOMPLETE
 * - Redirects to home if profile is already complete
 * - Redirects to correct onboarding step based on profileState
 */
export const OnboardingRoute: React.FC = () => {
  const { isAuthenticated, loading, initializing, isProfileComplete, profileState } = useAuth();
  const location = useLocation();

  // Show loading during initial auth check
  if (initializing || loading) {
    return <LoadingScreen />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // Profile already complete - redirect to home
  if (isProfileComplete) {
    return <Navigate to="/" replace />;
  }

  // Ensure user is on the correct onboarding step
  if (profileState) {
    const correctRoute = getOnboardingRoute(profileState);
    if (location.pathname !== correctRoute) {
      return <Navigate to={correctRoute} replace />;
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;

